import { useState, useEffect } from 'react';
import { Phone, Menu, X, Calendar, Download, Facebook } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_7a187c08-1566-4a5d-94a6-902036d37fa8/artifacts/oin9oc2t_Untitled%20design%20%2814%29.png";
const BROCHURE_URL = "https://customer-assets.emergentagent.com/job_poly-laundry-pro/artifacts/d7wpusxn_0-2.png";
const FACEBOOK_URL = "https://www.facebook.com/share/18aF5rQHCS/?mibextid=wwXIfr";

const navLinks = [
  { label: 'Home', href: '#hero' },
  { label: 'Services', href: '#services' },
  { label: 'Pickup & Delivery', href: '#how-it-works' },
  { label: 'Commercial', href: '#commercial' },
  { label: 'About', href: '#about' },
  { label: 'FAQ', href: '#faq' },
  { label: 'Contact', href: '#contact' },
];

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (e, href) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    const el = document.querySelector(href);
    if (el) {
      const offset = 80;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  return (
    <>
      <header
        data-testid="main-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'glass bg-white/90 shadow-sm border-b border-slate-200/50'
            : 'bg-white/0'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo — bigger, on white circle */}
            <a
              href="#hero"
              onClick={(e) => scrollToSection(e, '#hero')}
              className="flex items-center gap-3 shrink-0"
              data-testid="header-logo"
            >
              <div className="bg-white rounded-xl p-1 shadow-lg ring-2 ring-white/60">
                <img
                  src={LOGO_URL}
                  alt="Poly Clean Center Laundromat Logo"
                  className="h-11 w-11 sm:h-14 sm:w-14 object-contain"
                  loading="eager"
                />
              </div>
              <div className={`hidden sm:block transition-colors ${isScrolled ? 'text-poly-blue-dark' : 'text-white'}`}>
                <span className="font-heading font-bold text-xl leading-tight block">Poly Clean</span>
                <span className="text-xs font-medium tracking-wider uppercase opacity-80">Center Laundromat</span>
              </div>
            </a>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => scrollToSection(e, link.href)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isScrolled
                      ? 'text-slate-600 hover:text-poly-blue hover:bg-slate-50'
                      : 'text-white/90 hover:text-white hover:bg-white/10'
                  }`}
                  data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.label}
                </a>
              ))}
            </nav>

            {/* Desktop CTA */}
            <div className="hidden lg:flex items-center gap-3">
              <a
                href={FACEBOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={`p-2 rounded-full transition-all hover:scale-110 ${
                  isScrolled
                    ? 'text-[#1877F2] hover:bg-blue-50'
                    : 'text-white hover:bg-white/10'
                }`}
                data-testid="header-facebook"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href={BROCHURE_URL}
                download="PolyCleanCenter-Brochure.png"
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full border transition-all ${
                  isScrolled
                    ? 'border-slate-200 text-poly-blue hover:bg-poly-bg'
                    : 'border-white/30 text-white hover:bg-white/10'
                }`}
                data-testid="header-brochure-btn"
              >
                <Download className="w-3.5 h-3.5" />
                Brochure
              </a>
              <a
                href="tel:8704254295"
                className={`flex items-center gap-2 text-sm font-semibold transition-colors ${
                  isScrolled ? 'text-poly-blue-dark' : 'text-white'
                }`}
                data-testid="header-phone"
              >
                <Phone className="w-4 h-4" />
                (870) 425-4295
              </a>
              <a
                href="#contact"
                onClick={(e) => scrollToSection(e, '#contact')}
                className="inline-flex items-center gap-2 bg-poly-orange hover:bg-orange-600 text-white font-semibold text-sm px-5 py-2.5 rounded-full transition-all shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40 hover:scale-105 active:scale-95"
                data-testid="header-cta-button"
              >
                <Calendar className="w-4 h-4" />
                Schedule Pickup
              </a>
            </div>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`lg:hidden p-2 rounded-lg transition-colors ${
                isScrolled ? 'text-poly-blue-dark' : 'text-white'
              }`}
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div
          className={`lg:hidden bg-white border-t border-slate-100 shadow-xl transition-all duration-300 overflow-hidden ${
            mobileMenuOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
          }`}
          data-testid="mobile-menu"
        >
          <nav className="px-4 py-4 space-y-1">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => scrollToSection(e, link.href)}
                className="block px-4 py-3 rounded-lg text-slate-700 hover:bg-poly-bg hover:text-poly-blue font-medium text-base"
                data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.label}
              </a>
            ))}
            <div className="pt-3 border-t border-slate-100 space-y-2">
              <a
                href={FACEBOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-3 text-[#1877F2] font-semibold"
                data-testid="mobile-facebook"
              >
                <Facebook className="w-5 h-5" />
                Follow Us on Facebook
              </a>
              <a
                href={BROCHURE_URL}
                download="PolyCleanCenter-Brochure.png"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-3 text-poly-blue font-semibold"
                data-testid="mobile-brochure-btn"
              >
                <Download className="w-5 h-5" />
                Download Brochure
              </a>
              <a
                href="tel:8704254295"
                className="flex items-center gap-2 px-4 py-3 text-poly-blue font-semibold"
                data-testid="mobile-phone"
              >
                <Phone className="w-5 h-5" />
                (870) 425-4295
              </a>
            </div>
          </nav>
        </div>
      </header>

      {/* Mobile Bottom CTA Bar */}
      <div className="fixed bottom-0 left-0 right-0 z-50 lg:hidden mobile-cta-bar bg-white border-t border-slate-200 px-4 py-3 flex gap-3" data-testid="mobile-cta-bar">
        <a
          href="tel:8704254295"
          className="flex-1 flex items-center justify-center gap-2 bg-poly-blue text-white font-semibold text-sm py-3 rounded-xl active:scale-95 transition-transform"
          data-testid="mobile-call-btn"
        >
          <Phone className="w-4 h-4" />
          Call Now
        </a>
        <a
          href="#contact"
          onClick={(e) => scrollToSection(e, '#contact')}
          className="flex-1 flex items-center justify-center gap-2 bg-poly-orange text-white font-semibold text-sm py-3 rounded-xl active:scale-95 transition-transform"
          data-testid="mobile-schedule-btn"
        >
          <Calendar className="w-4 h-4" />
          Schedule Pickup
        </a>
      </div>
    </>
  );
}
